import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { j202Api, FJ120 } from '../../../services/j202Api';

interface UseJ202ApprovalOptions {
  record: FJ120 | null;
  isEditing: boolean;
  loadRecord: (id: string) => Promise<void>;
}

export function useJ202Approval({ record, isEditing, loadRecord }: UseJ202ApprovalOptions) {
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將繳庫單 ${record.fj120_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j202Api.submitForApproval(record.fj120_01);
          message.success(resp.message || '送審成功');
          await loadRecord(record.fj120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准繳庫單 ${record.fj120_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j202Api.approve(record.fj120_01);
          message.success(resp.message || '核准成功');
          await loadRecord(record.fj120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回繳庫單 ${record.fj120_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j202Api.reject(record.fj120_01);
          message.success(resp.message || '退回成功');
          await loadRecord(record.fj120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fj120_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j202Api.writeoff(record.fj120_01);
          message.success(resp.message || '沖銷成功');
          await loadRecord(record.fj120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleViewHistory = useCallback(() => {
    if (!record) return;
    setApprovalHistoryVisible(true);
  }, [record]);

  return {
    approvalHistoryVisible,
    setApprovalHistoryVisible,
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
  };
}
